import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../hooks/redux';
import { Button } from './ui/Button';
import { Dropdown } from './ui/Dropdown';

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low - Minor issue, not urgent' },
  { value: 'medium', label: 'Medium - Moderate impact' },
  { value: 'high', label: 'High - Critical issue requiring immediate attention' },
];

interface Attachment {
  filename: string;
  originalName: string;
  s3Key: string;
  s3Url: string;
  cloudFrontUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
}

interface SupportTicket {
  id: string;
  firebaseUid: string;
  name: string;
  email: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  attachments: Attachment[];
  adminNotes?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; description: string; severity: 'low' | 'medium' | 'high'; files: File[]; filesToDelete?: string[] }) => Promise<void>;
  ticket?: SupportTicket | null;
  submitting: boolean;
  error: string | null;
}

const SupportTicketModal: React.FC<SupportTicketModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  ticket,
  submitting,
  error
}) => {
  const { user, mongoUser } = useAppSelector((state) => state.auth);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high'>('medium');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<string[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);

  const isEditMode = !!ticket;

  useEffect(() => {
    if (ticket) {
      setTitle(ticket.title);
      setDescription(ticket.description);
      setSeverity(ticket.severity);
    } else {
      resetForm();
    }
  }, [ticket]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSeverity('medium');
    setSelectedFiles([]);
    setAttachmentsToDelete([]);
    setLocalError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      // Validate file sizes (max 10MB each)
      const maxSize = 10 * 1024 * 1024;
      const invalidFiles = files.filter(file => file.size > maxSize);

      if (invalidFiles.length > 0) {
        setLocalError(`Some files exceed the 10MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
        return;
      }

      // Max 5 files
      if (files.length > 5) {
        setLocalError('Maximum 5 files allowed');
        return;
      }

      setSelectedFiles(files);
      setLocalError(null);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const markAttachmentForDeletion = (s3Key: string) => {
    if (!attachmentsToDelete.includes(s3Key)) {
      setAttachmentsToDelete(prev => [...prev, s3Key]);
    }
  };

  const unmarkAttachmentForDeletion = (s3Key: string) => {
    setAttachmentsToDelete(prev => prev.filter(key => key !== s3Key));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      setLocalError('Title and description are required');
      return;
    }

    await onSubmit({
      title,
      description,
      severity,
      files: selectedFiles,
      filesToDelete: attachmentsToDelete.length > 0 ? attachmentsToDelete : undefined
    });
  };

  const handleClose = () => {
    onClose();
    if (!isEditMode) {
      resetForm();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="max-w-2xl w-full max-h-[85vh] overflow-y-auto modal-panel">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="heading-2">
              {isEditMode ? 'Edit Support Ticket' : 'Create Support Ticket'}
            </h2>
            <Button variant="tertiary" size="sm" onClick={handleClose} className="!p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>

          {(error || localError) && (
            <div className="mb-4 bg-error-tint border border-error text-ink px-4 py-3 rounded-lg">
              {error || localError}
            </div>
          )}

          {isEditMode && (ticket.status === 'resolved' || ticket.status === 'closed') && (
            <div className="mb-4 bg-warning-tint border border-warning text-ink px-4 py-3 rounded-lg">
              This ticket is {ticket.status}. You cannot edit it.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Auto-filled user info */}
            <div className="mb-4 p-4 bg-surface-subtle rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-secondary">Name</label>
                  <div className="mt-1 text-sm text-ink">{mongoUser?.name || 'N/A'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-secondary">Email</label>
                  <div className="mt-1 text-sm text-ink">{mongoUser?.email || user?.email || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Title <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border border-ink-border rounded-lg focus:ring-2 focus:ring-signature focus:border-transparent"
                placeholder="Brief summary of your issue"
                required
                disabled={isEditMode && (ticket.status === 'resolved' || ticket.status === 'closed')}
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Description <span className="text-error">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                className="w-full px-4 py-2 border border-ink-border rounded-lg focus:ring-2 focus:ring-signature focus:border-transparent"
                placeholder="Detailed description of your issue..."
                required
                disabled={isEditMode && (ticket.status === 'resolved' || ticket.status === 'closed')}
              />
            </div>

            {/* Severity */}
            <div className="mb-4">
              <label id="support-ticket-severity-label" className="block text-sm font-medium text-ink-secondary mb-1">
                Severity <span className="text-error">*</span>
              </label>
              <Dropdown
                labelledBy="support-ticket-severity-label"
                width="field"
                required
                value={severity}
                onChange={(next) => setSeverity(next as 'low' | 'medium' | 'high')}
                disabled={isEditMode && (ticket.status === 'resolved' || ticket.status === 'closed')}
                options={SEVERITY_OPTIONS}
              />
            </div>

            {/* Existing Attachments (Edit Mode Only) */}
            {isEditMode && ticket.attachments && ticket.attachments.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink-secondary mb-2">
                  Existing Attachments
                </label>
                <div className="space-y-2">
                  {ticket.attachments.map((attachment, index) => {
                    const isMarkedForDeletion = attachmentsToDelete.includes(attachment.s3Key);
                    const isTicketClosed = ticket.status === 'resolved' || ticket.status === 'closed';

                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-2 rounded-lg ${
                          isMarkedForDeletion ? 'bg-error-tint opacity-50' : 'bg-surface-subtle'
                        }`}
                      >
                        <a
                          href={attachment.cloudFrontUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center flex-1 hover:opacity-80"
                        >
                          <svg className="w-5 h-5 text-ink-muted mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span className={`text-sm ${isMarkedForDeletion ? 'text-error line-through' : 'text-signature'}`}>
                            {attachment.originalName}
                          </span>
                          <span className="ml-2 text-xs text-ink-muted">
                            ({formatFileSize(attachment.fileSize)})
                          </span>
                        </a>

                        {!isTicketClosed && (
                          <Button
                            type="button"
                            variant="tertiary"
                            size="sm"
                            onClick={() => {
                              if (isMarkedForDeletion) {
                                unmarkAttachmentForDeletion(attachment.s3Key);
                              } else {
                                markAttachmentForDeletion(attachment.s3Key);
                              }
                            }}
                            className={`!p-1 ml-2 ${
                              isMarkedForDeletion
                                ? 'text-ink-muted hover:text-ink-secondary'
                                : 'text-error hover:opacity-80'
                            }`}
                            title={isMarkedForDeletion ? 'Undo delete' : 'Delete attachment'}
                          >
                            {isMarkedForDeletion ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {attachmentsToDelete.length > 0 && (
                  <p className="mt-2 text-sm text-error">
                    {attachmentsToDelete.length} file(s) will be deleted when you update the ticket
                  </p>
                )}
              </div>
            )}

            {/* File Upload */}
            {!(isEditMode && (ticket.status === 'resolved' || ticket.status === 'closed')) && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  {isEditMode ? 'Add More Attachments (Optional)' : 'Attachments (Optional)'}
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-ink-border border-dashed rounded-lg">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-ink-muted"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-ink-secondary">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-surface rounded-md font-medium text-signature hover:text-signature-strong"
                      >
                        <span>Upload files</span>
                        <input
                          id="file-upload"
                          type="file"
                          className="sr-only"
                          multiple
                          onChange={handleFileChange}
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-ink-muted">
                      PNG, JPG, PDF, DOC, XLS up to 10MB each (max 5 files)
                    </p>
                  </div>
                </div>

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-surface-subtle rounded-lg"
                      >
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-ink-muted mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-ink-secondary">{file.name}</span>
                          <span className="ml-2 text-xs text-ink-muted">
                            ({formatFileSize(file.size)})
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="tertiary"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="!p-1 text-error hover:opacity-80"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={submitting || (isEditMode && (ticket.status === 'resolved' || ticket.status === 'closed'))}
              >
                {submitting ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update Ticket' : 'Submit Ticket')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketModal;
